/**
 * Inline template picker for the Projects agent.
 *
 * Used when the agent is unsure which template to generate a note /
 * document from: it asks ``ask_user`` with ``kind: 'pick_template'`` and
 * this list renders the user's own templates (self-fetched — the agent
 * doesn't enumerate them) so the clinician can choose. The selected
 * ``{ id, name }`` is handed back via ``onSelect`` and the clarification
 * resolves with the chosen ``template_id``.
 *
 * Standalone + presentational: it owns the fetch + search + selection
 * highlight, but not the submit — the parent decides what "picking"
 * means (here: arm the clarification's Continue button). The templates
 * list is the templates domain's, so it crosses as the ``useTemplates``
 * hook port.
 */

import { SearchInput } from '@heidi/design-system/search-input';
import { TypographyCaption } from '@heidi/design-system/typography';
import { TemplateCategory } from '@heidi/schema/template-gallery';
import { cn } from '@heidi/utils';
import { Check, FileText, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { templatePickerListMessages as m } from '../../../data/projects/components/templatePickerList.messages';

export interface PickedTemplate {
  id: string;
  name: string;
}

/** A template as the picker reads it: the fields it lists, searches and filters by. */
export interface PickerTemplate {
  id: string;
  name?: string | null;
  template_category?: string | null;
}

/** What the app supplies: the user's document templates query. */
export interface TemplatePickerListHost {
  /** Called unconditionally. Module-level, so its identity is stable. */
  useTemplates: () => {
    data?: readonly PickerTemplate[];
    isLoading: boolean;
    isError: boolean;
  };
}

export interface TemplatePickerListProps {
  /** Currently-armed template id (highlights the row). */
  selectedId?: string;
  onSelect: (template: PickedTemplate) => void;
  disabled?: boolean;
  /** Cap the visible list height; the list scrolls past this. */
  className?: string;
  /** Omit PDF form templates (document templates only). */
  excludeFormTemplates?: boolean;
}

const FORM_TEMPLATE = TemplateCategory.FORM_TEMPLATE as string;

const templateName = (t: PickerTemplate, untitled: string): string =>
  (t.name ?? '').trim() || untitled;

const isFormTemplate = (t: PickerTemplate): boolean =>
  isFormTemplateCategory(t.template_category);

const isFormTemplateCategory = (category: string | null | undefined): boolean =>
  category === FORM_TEMPLATE;

export const TemplatePickerList = ({
  selectedId,
  onSelect,
  disabled = false,
  className,
  excludeFormTemplates = false,
  useTemplates,
}: TemplatePickerListProps & TemplatePickerListHost): JSX.Element => {
  const intl = useIntl();
  const untitled = intl.formatMessage(m.untitledTemplate);
  const { data: templates, isLoading } = useTemplates();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    let list = templates ?? [];
    if (excludeFormTemplates) {
      list = list.filter((t) => !isFormTemplate(t));
    }
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((t) => templateName(t, untitled).toLowerCase().includes(q));
  }, [excludeFormTemplates, templates, query, untitled]);

  return (
    <div className={cn('flex flex-col gap-2', className)} data-testid="template-picker">
      <SearchInput
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        disabled={disabled || isLoading}
        placeholder={intl.formatMessage(m.searchPlaceholder)}
        aria-label={intl.formatMessage(m.searchAria)}
        clearAriaLabel={intl.formatMessage({
          defaultMessage: 'Clear search',
          id: '4YJHutR26p',
        })}
      />

      <div className="flex max-h-64 flex-col gap-1 overflow-y-auto [scrollbar-gutter:stable]">
        {isLoading ? (
          <div className="flex items-center gap-2 px-3 py-4 text-text-secondary">
            <Loader2 className="size-4 animate-spin" />
            <TypographyCaption>
              <FormattedMessage {...m.loadingTemplates} />
            </TypographyCaption>
          </div>
        ) : filtered.length === 0 ? (
          <TypographyCaption className="px-3 py-4 text-text-secondary">
            {query.trim()
              ? intl.formatMessage(m.noSearchResults)
              : intl.formatMessage(m.noTemplates)}
          </TypographyCaption>
        ) : (
          filtered.map((t) => {
            const isSelected = t.id === selectedId;
            return (
              <button
                key={t.id}
                type="button"
                disabled={disabled}
                onClick={() => onSelect({ id: t.id, name: templateName(t, untitled) })}
                aria-pressed={isSelected}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors',
                  'disabled:cursor-not-allowed disabled:opacity-60',
                  isSelected
                    ? 'border-border-pressed bg-background-secondary'
                    : 'border-border bg-surface hover:bg-surface-hover'
                )}
                data-testid="template-picker-row"
              >
                <FileText className="size-4 shrink-0 text-text-secondary" />
                <span className="min-w-0 flex-1 truncate text-sm text-text-primary">
                  {templateName(t, untitled)}
                </span>
                {isSelected && (
                  <Check className="size-4 shrink-0 text-text-active" aria-hidden />
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

/**
 * Resolves a bare ``pick_template`` Answers-card id via the same
 * document-templates list the picker uses, read through the same port.
 */
export const ClarificationAnswerTemplateLabel = ({
  templateId,
  useTemplates,
}: {
  templateId: string;
} & TemplatePickerListHost): JSX.Element => {
  const intl = useIntl();
  const untitled = intl.formatMessage(m.untitledTemplate);
  const { data: templates, isLoading, isError } = useTemplates();
  const match = (templates ?? []).find((template) => template.id === templateId);
  const name = match === undefined ? undefined : templateName(match, untitled);

  // Same loading vs idle rule as ClarificationAnswerSessionLabel: disabled
  // TanStack queries stay pending forever; only in-flight fetches count as loading.
  if (isLoading && templates === undefined) {
    return (
      <span
        className="inline-flex items-center gap-2 text-sm text-text-secondary"
        data-testid="clarification-template-answer"
        data-status="loading"
      >
        <Loader2 className="size-3.5 animate-spin" aria-hidden />
        <FormattedMessage {...m.loading} />
      </span>
    );
  }

  if (isError || name === undefined) {
    return (
      <span
        className="text-sm text-text-secondary"
        data-testid="clarification-template-answer"
        data-status="unavailable"
      >
        <FormattedMessage {...m.templateUnavailable} />
      </span>
    );
  }

  return (
    <span
      className="break-words text-sm text-text-primary"
      data-testid="clarification-template-answer"
      data-status="ready"
    >
      {name}
    </span>
  );
};
